import _asyncToGenerator from "/app/node_modules/.pnpm/next@12.2.0_@babel+core@7.16.0_react-dom@17.0.2_react@17.0.2/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _regeneratorRuntime from "/app/node_modules/.pnpm/next@12.2.0_@babel+core@7.16.0_react-dom@17.0.2_react@17.0.2/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";
import { useCallback, useMemo, useRef } from 'react';
import { useHasPendingApproval, useTransactionAdder } from "../state/transactions/hooks";
import { FixedPointNumber } from "../utils/fixedPoint";
import { useTokenContract } from '.';
import { AddressZero, MaxUint256 } from '@ethersproject/constants';
import { useTokenAllowance } from "./useTokenAllowance";
import { TransactionType } from "../state/transactions/actions";
import { gasSpeedup } from "../utils/gasSpeedup";
import { useApprovalModalStateDispatch } from "../state/application/hooks";
import { ApprovalProgress } from "../components/ApprovalModal/ApproveStatusLabel";
import { useWeb3React } from '@web3-react/core';
import { useAllowanceTarget } from "../state/dex/hooks";
export var useApproveCallback = function useApproveCallback(tokenToApprove, amountToApprove, spender) {
  var _useWeb3React = useWeb3React(),
      chainId = _useWeb3React.chainId,
      provider = _useWeb3React.provider;

  var tokenContract = useTokenContract(tokenToApprove === null || tokenToApprove === void 0 ? void 0 : tokenToApprove.address);
  var timeoutRef = useRef();
  var approvalState = useApprovalState(tokenToApprove, amountToApprove, spender);
  var addTransaction = useTransactionAdder();
  var updateApprovalState = useApprovalModalStateDispatch();
  var approve = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var gasPrice, _feeData$gasPrice, feeData, estimateGas;

    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }

            if (tokenContract) {
              _context.next = 4;
              break;
            }

            console.error('Token contract is null');
            return _context.abrupt("return");

          case 4:
            if (chainId) {
              _context.next = 7;
              break;
            }

            console.error('Chain ID is null');
            return _context.abrupt("return");

          case 7:
            if (spender) {
              _context.next = 10;
              break;
            }

            console.error('Spender is not specified');
            return _context.abrupt("return");

          case 10:
            if (amountToApprove) {
              _context.next = 13;
              break;
            }

            console.error('Amount to approve is not specified');
            return _context.abrupt("return");

          case 13:
            updateApprovalState({
              progress: amountToApprove.isZero() ? ApprovalProgress.CANCEL_APPROVING : ApprovalProgress.APPROVING,
              hash: ''
            });
            gasPrice = undefined;
            _context.prev = 15;
            _context.next = 18;
            return provider.getFeeData();

          case 18:
            feeData = _context.sent;
            gasPrice = (_feeData$gasPrice = feeData === null || feeData === void 0 ? void 0 : feeData.gasPrice) !== null && _feeData$gasPrice !== void 0 ? _feeData$gasPrice : null;
            _context.next = 25;
            break;

          case 22:
            _context.prev = 22;
            _context.t0 = _context["catch"](15);
            console.warn('failed to get gasPrice');

          case 25:
            console.log(gasPrice);
            console.log(!gasPrice ? 'not found gasPrice' : 'gasPrice ' + gasPrice.toString());
            _context.next = 29;
            return tokenContract.estimateGas.approve(spender, amountToApprove)["catch"](function () {
              return tokenContract.estimateGas.approve(spender, MaxUint256);
            });

          case 29:
            estimateGas = _context.sent;
            return _context.abrupt("return", tokenContract.approve(spender, amountToApprove.toString(), {
              gasLimit: gasSpeedup(estimateGas),
              // @notice: 这里手动加 gasPrice 是因为 imToken 2.10.3 版本，arbitrum 网络 App 内获取 gasPrice 有延迟
              // 可能会出现 gas 过低的情况，所以这边手动通过 rpc 获取 gasPrice
              gasPrice: gasPrice
            }).then(function (response) {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }

              addTransaction(response, {
                type: TransactionType.APPROVAL,
                tokenAddress: tokenToApprove === null || tokenToApprove === void 0 ? void 0 : tokenToApprove.address,
                spender: spender,
                amountToApprove: amountToApprove.toString()
              });
              updateApprovalState({
                hash: response.hash
              });
            })["catch"](function (error) {
              console.error('allowance failed', error);

              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }

              updateApprovalState({
                progress: amountToApprove.isZero() ? ApprovalProgress.CANCEL_REJECTED : ApprovalProgress.APPROVE_REJECTED
              });
            }));

          case 31:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[15, 22]]);
  })), [approvalState, tokenToApprove, amountToApprove, spender, addTransaction, updateApprovalState, chainId, tokenContract]);
  return [approvalState, approve];
};
export var ApprovalState;

(function (ApprovalState) {
  ApprovalState["UNKNOWN"] = "UNKNOWN";
  ApprovalState["NOT_APPROVED"] = "NOT_APPROVED";
  ApprovalState["PENDING"] = "PENDING";
  ApprovalState["APPROVED"] = "APPROVED";
})(ApprovalState || (ApprovalState = {}));

export var useApprovalState = function useApprovalState(tokenToApprove, amountToApprove, spender) {
  var _useWeb3React2 = useWeb3React(),
      account = _useWeb3React2.account;

  var currentAllowance = useTokenAllowance(tokenToApprove, account !== null && account !== void 0 ? account : undefined, spender);
  var pendingApproval = useHasPendingApproval(tokenToApprove === null || tokenToApprove === void 0 ? void 0 : tokenToApprove.address, spender);
  return useMemo(function () {
    if (!amountToApprove || !spender) return ApprovalState.UNKNOWN;
    if ((tokenToApprove === null || tokenToApprove === void 0 ? void 0 : tokenToApprove.address) === AddressZero) return ApprovalState.UNKNOWN;
    if (!currentAllowance) return ApprovalState.UNKNOWN;
    return currentAllowance.isLessThan(new FixedPointNumber(amountToApprove)) ? pendingApproval ? ApprovalState.PENDING : ApprovalState.NOT_APPROVED : ApprovalState.APPROVED;
  }, [amountToApprove, currentAllowance, pendingApproval, spender]);
};
export var useNeedReduceAllowance2Zero = function useNeedReduceAllowance2Zero(tokenToApprove) {
  var tokenContract = useTokenContract(tokenToApprove === null || tokenToApprove === void 0 ? void 0 : tokenToApprove.address);
  var allowanceTarget = useAllowanceTarget();
  return useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var estimateGas;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            _context2.next = 3;
            return tokenContract.estimateGas.approve(allowanceTarget, MaxUint256);

          case 3:
            estimateGas = _context2.sent;
            return _context2.abrupt("return", !estimateGas);

          case 7:
            _context2.prev = 7;
            _context2.t0 = _context2["catch"](0);
            return _context2.abrupt("return", true);

          case 10:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, null, [[0, 7]]);
  })), [tokenToApprove, allowanceTarget, tokenContract]);
};